/**
 * Custom AG Code - Nameplate Upsell JavaScript
 * Handles the custom tree nameplate upsell functionality for Abandoned Grove
 * 
 * *** CUSTOM ABANDONED GROVE TEAM CODE ***
 * This is custom functionality for the nameplate upsell feature
 * Integrates with theme's existing cart system to prevent conflicts
 */

class NameplateUpsell {
  constructor() {
    this.isSubmitting = false;
    this.debug = true; // Enable detailed logging
    this.nameplateVariantId = null;
    this.init();
  }

  init() {
    this.findNameplateVariantId();
    this.bindEvents();
    this.setupFormInterception();
    this.log('NameplateUpsell initialized');
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[NameplateUpsell] ${message}`, data || '');
    }
  }

  findNameplateVariantId() {
    // Get variant ID from any nameplate checkbox
    const checkbox = document.querySelector('[data-nameplate-checkbox]');
    if (checkbox && checkbox.dataset.variantId) {
      this.nameplateVariantId = parseInt(checkbox.dataset.variantId);
      this.log('Found nameplate variant ID:', this.nameplateVariantId);
    } else {
      this.log('Warning: No nameplate variant ID found');
    }
  }

  bindEvents() {
    // Handle checkbox changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-nameplate-checkbox]')) {
        this.handleCheckboxChange(e.target);
      }
    });

    // Handle text input changes
    document.addEventListener('input', (e) => {
      if (e.target.matches('[data-nameplate-text]')) {
        this.handleTextInput(e.target);
      }
    });
  }

  handleCheckboxChange(checkbox) {
    const upsellContainer = checkbox.closest('[data-nameplate-upsell]');
    const engravingSection = upsellContainer.querySelector('[data-nameplate-engraving]');
    const textInput = upsellContainer.querySelector('[data-nameplate-text]');

    // Prevent the checkbox change from bubbling up to other form elements
    checkbox.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    if (checkbox.checked) {
      // Show engraving section
      engravingSection.style.display = 'block';
      upsellContainer.classList.add('nameplate-upsell--selected');
      
      // Focus on text input after a short delay
      setTimeout(() => {
        textInput.focus();
      }, 300);
    } else {
      // Hide engraving section and clear text
      engravingSection.style.display = 'none';
      upsellContainer.classList.remove('nameplate-upsell--selected');
      textInput.value = '';
    }
  }

  handleTextInput(textInput) {
    // Basic validation - 40 character limit
    const value = textInput.value;
    if (value.length > 40) {
      textInput.value = value.substring(0, 40);
    }
  }

  setupFormInterception() {
    // Intercept ALL product form submissions
    document.addEventListener('submit', (e) => {
      // Check if this is a product form
      if (e.target.matches('.product-single__form') || 
          e.target.matches('form[action*="/cart/add"]') ||
          e.target.matches('form[action="/cart/add"]') ||
          e.target.classList.contains('product-form')) {
        this.handleFormSubmission(e);
      }
    });
  }

  async handleFormSubmission(e) {
    const form = e.target;
    const nameplateCheckbox = form.querySelector('[data-nameplate-checkbox]');
    
    this.log('Form submission detected', {
      hasNameplateCheckbox: !!nameplateCheckbox,
      isChecked: nameplateCheckbox?.checked,
      formAction: form.action
    });

    // If no nameplate checkbox or not checked, let normal submission proceed
    if (!nameplateCheckbox || !nameplateCheckbox.checked) {
      this.log('No nameplate selected, allowing normal form submission');
      return; // Let the theme handle normal submission
    }

    // Nameplate is selected - we need to handle this specially
    e.preventDefault();
    e.stopPropagation();

    // Prevent double submissions
    if (this.isSubmitting) {
      this.log('Submission already in progress, blocking duplicate');
      return;
    }

    const nameplateName = form.querySelector('[data-nameplate-text]').value.trim();
    
    if (!nameplateName) {
      alert('Please enter text for your nameplate or uncheck the nameplate option.');
      return;
    }

    if (!this.nameplateVariantId) {
      alert('Nameplate product not properly configured. Please contact support.');
      return;
    }

    try {
      this.isSubmitting = true;
      await this.addProductsSequentially(form, nameplateCheckbox, nameplateName);
    } catch (error) {
      this.log('Error in form submission:', error);
      alert('There was an error adding the products to your cart. Please try again.');
    } finally {
      this.isSubmitting = false;
    }
  }

  async addProductsSequentially(form, nameplateCheckbox, nameplateName) {
    this.log('Starting sequential product addition');

    // Step 1: Add the main product first using theme's method
    const mainProduct = await this.addMainProduct(form, nameplateName);
    this.log('Main product added:', mainProduct);

    // Step 2: Add nameplate as a separate addition
    const nameplate = await this.addNameplateProduct(nameplateName, form);
    this.log('Nameplate added:', nameplate);

    // Step 3: Use theme's success flow
    this.handleSuccessfulAddition(form, nameplateCheckbox);
  }

  async addMainProduct(form, nameplateName) {
    const formData = new FormData(form);
    
    // Add nameplate metadata to main product
    formData.append('properties[_has_nameplate]', 'true');
    formData.append('properties[_nameplate_text]', nameplateName);

    // Convert FormData to URLSearchoded (theme expects this format)
    const urlSearchParams = new URLSearchParams();
    for (let [key, value] of formData.entries()) {
      urlSearchParams.append(key, value);
    }

    this.log('Adding main product with data:', Object.fromEntries(formData.entries()));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: urlSearchParams,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.log('Main product add failed:', errorText);
      throw new Error('Failed to add main product');
    }

    return await response.json();
  }

  async addNameplateProduct(nameplateName, form) {
    const formData = new FormData(form);
    const urlSearchParams = new URLSearchParams();
    
    // Add nameplate with its properties
    urlSearchParams.append('id', this.nameplateVariantId);
    urlSearchParams.append('quantity', '1');
    urlSearchParams.append('properties[Engraving Text]', nameplateName);
    urlSearchParams.append('properties[_nameplate_addon]', 'true');

    this.log('Adding nameplate product with data:', Object.fromEntries(urlSearchParams.entries()));

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      body: urlSearchParams,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      this.log('Nameplate add failed:', errorText);
      throw new Error('Failed to add nameplate');
    }

    return await response.json();
  }

  handleSuccessfulAddition(form, nameplateCheckbox) {
    this.log('Both products added successfully, triggering theme cart update');

    // Reset nameplate form state
    this.resetFormState(form, nameplateCheckbox);

    // Use the theme's existing cart update system
    this.triggerThemeCartUpdate(form);
  }

  resetFormState(form, nameplateCheckbox) {
    // Reset nameplate checkbox and text
    nameplateCheckbox.checked = false;
    const textInput = form.querySelector('[data-nameplate-text]');
    if (textInput) {
      textInput.value = '';
    }
    
    // Hide engraving section
    const engravingSection = form.querySelector('[data-nameplate-engraving]');
    if (engravingSection) {
      engravingSection.style.display = 'none';
    }
    
    // Remove selected class
    const upsellContainer = nameplateCheckbox.closest('[data-nameplate-upsell]');
    if (upsellContainer) {
      upsellContainer.classList.remove('nameplate-upsell--selected');
    }
  }

  triggerThemeCartUpdate(form) {
    // Method 1: Prevent image flickering during cart update
    this.preventCartImageFlickering();

    // Method 2: Trigger the theme's ajaxProduct:added event
    // This is what the theme expects after a successful cart addition
    const addToCartBtn = form.querySelector('.add-to-cart, [type="submit"]');
    
    if (addToCartBtn) {
      form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: { id: this.nameplateVariantId }, // Mock product data
          addToCartBtn: addToCartBtn
        },
        bubbles: true
      }));
    }

    // Method 3: Trigger cart drawer update specifically
    // The theme listens for this event to rebuild and open the cart
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: { id: this.nameplateVariantId },
          addToCartBtn: addToCartBtn
        }
      }));
    }, 100);

    // Method 4: Force cart rebuild using theme's method
    setTimeout(() => {
      if (window.theme && window.theme.CartDrawer && typeof document.dispatchEvent === 'function') {
        document.dispatchEvent(new CustomEvent('cart:build'));
      }
    }, 200);

    // Method 5: Re-enable image animations after cart has settled
    setTimeout(() => {
      this.restoreCartImageAnimations();
    }, 800);

    this.log('Theme cart update events triggered');
  }

  preventCartImageFlickering() {
    // Temporarily disable image animations in cart drawer to prevent white flashing
    const style = document.createElement('style');
    style.id = 'nameplate-cart-fix';
    style.textContent = `
      /* Custom AG Code - Prevent cart image flickering during nameplate upsell */
      #CartDrawer .image-element,
      #CartDrawer .cart__item--image .image-element,
      .drawer .image-element {
        opacity: 1 !important;
        animation: none !important;
        transition: none !important;
      }
      
      #CartDrawer .appear-animation {
        animation-duration: 0.1s !important;
      }
    `;
    document.head.appendChild(style);
    this.log('Cart image flickering prevention enabled');
  }

  restoreCartImageAnimations() {
    // Remove the temporary style fix to restore normal animations
    const tempStyle = document.getElementById('nameplate-cart-fix');
    if (tempStyle) {
      tempStyle.remove();
      this.log('Cart image animations restored');
    }
  }

  // Utility method to update variant ID after product creation  
  updateNameplateVariantId(variantId) {
    this.nameplateVariantId = parseInt(variantId);
    const checkboxes = document.querySelectorAll('[data-nameplate-checkbox]');
    checkboxes.forEach(checkbox => {
      checkbox.dataset.variantId = variantId;
    });
    this.log('Nameplate variant ID updated to:', variantId);
  }
}

// Initialize when DOM is ready (prevent multiple instances)
if (!window.nameplateUpsellInstance) {
  document.addEventListener('DOMContentLoaded', () => {
    window.nameplateUpsellInstance = new NameplateUpsell();
  });
}

// Also handle dynamic content loading (AJAX, etc.)
document.addEventListener('shopify:section:load', () => {
  if (!window.nameplateUpsellInstance) {
    window.nameplateUpsellInstance = new NameplateUpsell();
  } else {
    window.nameplateUpsellInstance.init();
  }
});

// Export for potential external use
window.NameplateUpsell = NameplateUpsell; 